/**
 * Constantes partagées, **non équilibrables**.
 *
 * Règle (DECISIONS §7 point 13) : aucune valeur d'équilibrage n'est écrite en dur dans le code.
 * Tout ce qui a un effet sur la difficulté (dégâts, portée, cadence, PV, vitesses, coûts,
 * courbes, probabilités) vit dans `packages/content/data/*.json` (`balance.json` inclus) et
 * n'a rien à faire ici. Ne doivent figurer ci-dessous que des constantes de contrat :
 * géométrie imposée, chemins d'API, noms de cookies, limites de validation.
 */
/** Préfixe de toutes les routes de l'API v1 (DECISIONS §4.1). */
export const API_BASE_PATH = '/api/v1';
/** Canvas logique imposé (DECISIONS §6.6). */
export const CANVAS = {
    largeur: 1280,
    hauteur: 720,
};
/** Grille imposée : 32 colonnes × 18 lignes de cellules de 40 px (DECISIONS §6.6). */
export const GRILLE = {
    colonnes: 32,
    lignes: 18,
    cellulePx: 40,
};
/** Nom du cookie de refresh token (DECISIONS §4.1) : HttpOnly, Secure hors dev, SameSite=Lax. */
export const COOKIE_REFRESH = 'elemora_rt';
/** Nom du cookie de langue (DECISIONS §9.3). */
export const COOKIE_LOCALE = 'elemora_locale';
/** En-tête d'idempotence exigé sur les routes financièrement sensibles (DECISIONS §4.1). */
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
/** En-tête d'authentification. */
export const AUTHORIZATION_HEADER = 'Authorization';
/** Schéma d'authentification attendu : `Bearer <jwt>`. */
export const BEARER_PREFIX = 'Bearer ';
/** Locales livrées en v1 (DECISIONS §9.3). La première est la locale par défaut. */
export const LOCALES = ['fr', 'en'];
export const LOCALE_DEFAUT = 'fr';
/** Namespaces i18next : interface et contenu (DECISIONS §9.1). */
export const I18N_NAMESPACES = ['ui', 'content'];
/** Préfixe des clés de contenu, ex. `content.carte.carte_feu_001.name` (DECISIONS §6.3, §9.2). */
export const CONTENT_KEY_PREFIX = 'content.';
/** Longueur minimale d'un mot de passe (DECISIONS §4.2 « Règles »). */
export const PASSWORD_MIN_LENGTH = 10;
/** Longueur maximale acceptée, garde-fou anti-DoS sur le hachage argon2id. */
export const PASSWORD_MAX_LENGTH = 200;
/** Bornes du pseudo. Non fixées par DECISIONS : valeurs de contrat minimales, documentées. */
export const PSEUDO_MIN_LENGTH = 3;
export const PSEUDO_MAX_LENGTH = 20;
/** Motif du pseudo : lettres/chiffres/tiret/tiret bas, sans espace (affichage public). */
export const PSEUDO_PATTERN = /^[A-Za-z0-9_-]+$/;
/**
 * Pseudo porté par un compte supprimé (anonymisation RGPD, DECISIONS §11).
 *
 * Il est **réservé** : aucune inscription ni modification ne peut le choisir, sinon un
 * compte actif empêcherait l'anonymisation d'un autre compte (l'index unique partiel
 * `users_pseudo_actif_unique` porte sur les comptes actifs).
 */
export const PSEUDO_ANONYMISE = 'ancien-joueur';
/** Pagination de `GET /me/runs` (DECISIONS §4.2 : `?limit=20`). Borne haute : 50. */
export const RUNS_PAGE_LIMIT_DEFAUT = 20;
export const RUNS_PAGE_LIMIT_MAX = 50;
/**
 * Bornes de l'historique dans l'export RGPD (`GET /me/export`, §11).
 *
 * L'export est un droit, pas un outil d'extraction de masse : 100 runs par défaut,
 * 500 au maximum. Une borne basse borne aussi la réponse (aucun export vide silencieux).
 */
export const EXPORT_RUNS_LIMIT_DEFAUT = 100;
export const EXPORT_RUNS_LIMIT_MAX = 500;
/** Quotas de débit (DECISIONS §4.1). */
export const RATE_LIMIT_AUTH_PAR_MINUTE = 5;
export const RATE_LIMIT_DEFAUT_PAR_MINUTE = 120;
/** TTL du JWT d'accès, en secondes (15 min, DECISIONS §4.1). */
export const ACCESS_TOKEN_TTL_SECONDES = 900;
/** TTL du refresh token, en secondes (30 jours, DECISIONS §4.1). */
export const REFRESH_TOKEN_TTL_SECONDES = 2_592_000;
/** Inactivité maximale d'une run avant expiration serveur (30 min, DECISIONS §7.4). */
export const RUN_INACTIVITE_MAX_MS = 1_800_000;
/** Raretés, de la plus commune à la plus rare (DECISIONS §6.4, §7 point 14). */
export const RARETES = ['commune', 'rare', 'legendaire', 'mythique'];
/** Éléments du jeu (DECISIONS §6.1). */
export const ELEMENTS = ['feu', 'eau', 'terre', 'air'];
/** Archétypes de tours (DECISIONS §6.2). */
export const ARCHETYPES = ['mono', 'zone', 'ligne', 'chain', 'aura'];
/** Valeurs de `type` d'un ennemi (DECISIONS §6.6). */
export const TYPES_ENNEMI = ['normal', 'rapide', 'cuirasse', 'volant', 'boss'];
/** Modes de jeu (DECISIONS §6.6). */
export const MODES = ['limite', 'illimite'];
/** Statuts d'une run (DECISIONS §5). */
export const STATUTS_RUN = ['active', 'validee', 'perdue', 'rejetee', 'expiree'];
/** Statuts d'une vague dans une run. */
export const STATUTS_VAGUE = ['en_attente', 'en_cours', 'close', 'invalide'];
/** Raisons de fin de run transmises par le client (DECISIONS §4.2). */
export const RAISONS_FIN_RUN = ['defaite', 'victoire', 'abandon'];
/**
 * Assemblage des chemins de l'API. Les chemins sont **relatifs à `API_BASE_PATH`** :
 * le client préfixe avec `VITE_API_BASE_URL`, le serveur monte le routeur sur `/api/v1`.
 */
export const API_PATHS = {
    auth: {
        register: '/auth/register',
        login: '/auth/login',
        refresh: '/auth/refresh',
        logout: '/auth/logout',
        emailVerify: '/auth/email/verify',
        passwordForgot: '/auth/password/forgot',
        passwordReset: '/auth/password/reset',
    },
    me: {
        root: '/me',
        collection: '/me/collection',
        runs: '/me/runs',
        export: '/me/export',
    },
    content: {
        manifest: '/content/manifest',
        level: (levelId) => `/content/levels/${encodeURIComponent(levelId)}`,
        card: (cardId) => `/content/cards/${encodeURIComponent(cardId)}`,
    },
    runs: {
        create: '/runs',
        byId: (runId) => `/runs/${encodeURIComponent(runId)}`,
        waveStart: (runId, index) => `/runs/${encodeURIComponent(runId)}/waves/${index}/start`,
        waveResult: (runId, index) => `/runs/${encodeURIComponent(runId)}/waves/${index}/result`,
        cardOptions: (runId) => `/runs/${encodeURIComponent(runId)}/cards/options`,
        cardChoose: (runId) => `/runs/${encodeURIComponent(runId)}/cards/choose`,
        towerPlace: (runId) => `/runs/${encodeURIComponent(runId)}/towers`,
        towerUpgrade: (runId, towerId) => `/runs/${encodeURIComponent(runId)}/towers/${encodeURIComponent(towerId)}/upgrade`,
        end: (runId) => `/runs/${encodeURIComponent(runId)}/end`,
    },
    admin: {
        login: '/admin/auth/login',
        cards: '/admin/cards',
        card: (cardId) => `/admin/cards/${encodeURIComponent(cardId)}`,
        balance: '/admin/balance',
        levels: '/admin/levels',
        level: (levelId) => `/admin/levels/${encodeURIComponent(levelId)}`,
        players: '/admin/players',
        contentPublish: '/admin/content/publish',
        audit: '/admin/audit',
    },
};
//# sourceMappingURL=constants.js.map